import { useState, useEffect, useRef } from "react";
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { Box, Flex, useToast } from "@chakra-ui/react";
import InputTextAreaExpandable from '../../../forms/TextareaField/components/InputTextAreaExpandable';
import LaunchdeckCloseButton from "../../../global/components/helper-components/components/LaunchdeckCloseButton";
import LaunchdeckButton from "../../../global/components/helper-components/components/LaunchdeckButton";
import { triggerLaunchdeckToast } from "../../../global/helpers/global-helpers";
import useLaunchdeckModal from "../../../global/hooks/use-launchdeck-modal";
import { manageDsrFaqContent, resetManageDsrFaqContent } from "../../actions/dsr-faq-actions";



function EditQuestionAndAnswerModule({
  placeholder,
  dsrId,
  questionId,
  handleCloseInputArea,
  currentQuestionTextValue,
  currentAnswerTextValue,
  //Actions
  manageDsrFaqContent,
  resetManageDsrFaqContent,
  faqProcessedEvent,
  // Redux
  isRoomTemplate,
  dsrTemplateId,
  isMobileView,
  isDsrAdmin,
  manageFaqProcessingCompleted
}) {

  const toast = useToast();
  const { openLaunchdeckModal } = useLaunchdeckModal();

  const originalQuestionValue = useRef(currentQuestionTextValue || null)
  const originalAnswerValue = useRef(currentAnswerTextValue || null)

  // Inputs
  const [inputQuestionValue, setInputQuestionValue] = useState(currentQuestionTextValue || "");
  const [inputAnswerValue, setInputAnswerValue] = useState(currentAnswerTextValue || "");

  // UX
  const [questionIsSaving, setQuestionIsSaving] = useState(false)
  const [displayEmptyInputError, setDisplayEmptyInputError] = useState(false)
  const [isInvalidInput, setIsInvalidInput] = useState("");


  useEffect(() => {
    if (inputQuestionValue && inputAnswerValue) {
      setDisplayEmptyInputError(false)
    }
  }, [inputQuestionValue, inputAnswerValue]);

  useEffect(() => {
    if (manageFaqProcessingCompleted === true && faqProcessedEvent) {

      if (faqProcessedEvent === "edit-faq-question" || faqProcessedEvent === "edit-faq-answer") {
        toast(
          triggerLaunchdeckToast({
            useCase: "show-success-state",
            bannerText: "Question updated",
            label: "Your changes have been saved",
            isMobileView
          })
        )

      }

      setInputQuestionValue(currentQuestionTextValue || "")
      setQuestionIsSaving(false)
      resetManageDsrFaqContent()
      handleCloseInputArea()


    } else if (manageFaqProcessingCompleted === false) {
      toast(
        triggerLaunchdeckToast({
          useCase: "show-error-state",
          bannerText: "Unexpected error encountered",
          label: "Unfortunately something went wrong. Please try again later.",
          isMobileView
        })
      )

      setQuestionIsSaving(false)
      resetManageDsrFaqContent()
    }
  }, [manageFaqProcessingCompleted, faqProcessedEvent, handleCloseInputArea])


  const onChangeHandler = event => {
    if (!inputQuestionValue || !inputAnswerValue) {
      setDisplayEmptyInputError(false)
    }

    if (event.target.name === "faqQuestion") {
      setInputQuestionValue(event.target.value);
    } else if (event.target.name === "faqAnswer") {
      setInputAnswerValue(event.target.value)
    }

    setIsInvalidInput("")
  };

  const handleEmptyInput = () => {
    setIsInvalidInput(true)
    setDisplayEmptyInputError(true)
  }

  const handleClickEditFaqQuestion = () => {
    if (!inputQuestionValue) {
      setDisplayEmptyInputError(true)
    }
    else if (originalQuestionValue !== inputQuestionValue) {
      const editFaqQuestionMetadata = {
        processEvent: 'edit-faq-question',
        payload: {
          dsrId: isRoomTemplate ? dsrTemplateId : dsrId,
          questionId: questionId,
          faqQuestionContent: inputQuestionValue,
          isRoomTemplate
        }
      }

      manageDsrFaqContent(editFaqQuestionMetadata)
      setQuestionIsSaving(true)
    }
  }

  const handleClickEditFaqAnswer = () => {
    if (!inputAnswerValue) {
      setDisplayEmptyInputError(true)
    } else if (originalAnswerValue !== inputAnswerValue) {
      const editFaqQuestionMetadata = {
        processEvent: 'edit-faq-answer',
        payload: {
          dsrId: isRoomTemplate ? dsrTemplateId : dsrId,
          questionId: questionId,
          faqAnswerContent: inputAnswerValue,
          isRoomTemplate
        }
      }

      manageDsrFaqContent(editFaqQuestionMetadata)

      setInputAnswerValue(currentAnswerTextValue || "")
      setQuestionIsSaving(true)
    }
  }


  const handleSave = () => {
    if (originalQuestionValue.current === inputQuestionValue?.trim() && originalAnswerValue.current === inputAnswerValue?.trim()) {
      handleCloseInputArea()
      setDisplayEmptyInputError(false)
    } else {
      if (originalQuestionValue.current !== inputQuestionValue?.trim()) {
        handleClickEditFaqQuestion()
      }

      if (originalAnswerValue.current !== inputAnswerValue?.trim()) {
        handleClickEditFaqAnswer()
      }
    }
  }

  const handleClickCloseButton = () => {
    handleCloseInputArea()
    setDisplayEmptyInputError(false)
    setInputAnswerValue("")
    setInputQuestionValue("")
  }

  const handleDeleteFaqQuestion = () => {
    openLaunchdeckModal("confirm-delete-faq-question", {
      dsrId,
      questionId
    })
  }


  return (
    <Box w='100%'>
      <>
        <Flex w='100%' justify='space-between' align='center' px='1em' pt='0.5em'>
          <Box fontSize='0.6em' p='0.25em 1em' bg='brandPink.500' color='white' fontWeight='600' rounded='0.5em'>EDIT MODE</Box>
          {!questionIsSaving && <LaunchdeckCloseButton handleCloseModal={handleClickCloseButton} />}
        </Flex>

        <Box p='1em'>
          <Flex w='100%' mb='0.5em' fontSize='0.75em' fontWeight='600' color='brandDark.500' justify='flex-start' px='0.5em' letterSpacing='1px'>QUESTION</Flex>
          <InputTextAreaExpandable
            name="faqQuestion"
            onChangeHandler={onChangeHandler}
            inputValue={inputQuestionValue}
            placeholder={placeholder}
            isInvalidInput={isInvalidInput}
            bg='white'
            isDisabled={questionIsSaving}
          />

          <Flex w='100%' mt='2em' mb='0.5em' fontSize='0.75em' fontWeight='600' color='brandDark.500' justify='flex-start' px='0.5em' letterSpacing='1px'>ANSWER</Flex>
          <InputTextAreaExpandable
            name="faqAnswer"
            onChangeHandler={onChangeHandler}
            inputValue={inputAnswerValue}
            placeholder={placeholder}
            isInvalidInput={isInvalidInput}
            bg='white'
            isDisabled={questionIsSaving}
          />
          {displayEmptyInputError && <Box fontSize="0.8em" color="brandPink.500" mt='0.2em'>Did you want to delete the question? Simply use the delete button below. </Box>}

          <Flex mt='0.5em' justify='flex-end' w='100%'>

            <Flex mt='0.5em' w='100%' justify={(isDsrAdmin || isRoomTemplate) ? 'space-between' : 'flex-end'} align='flex-end'>
              {(isDsrAdmin || isRoomTemplate) &&
                <Box mr='1em' color='gray.500' cursor='pointer' fontSize='0.75em' onClick={() => handleDeleteFaqQuestion()}>
                  Delete this entire question & answer
                </Box>
              }
              <LaunchdeckButton bg='brand.500' color='white' fontSize='0.8em' loadingLogic={questionIsSaving}
                onClick={() => (inputAnswerValue && inputQuestionValue) ? handleSave() : handleEmptyInput()} label={'Save changes'} />
            </Flex>

          </Flex>
        </Box>
      </>
    </Box>
  )

}


function mapStateToProps(state) {
  return {
    dsrTemplateId: state.dsrTemplateId.results,
    isRoomTemplate: state.dsrTemplateId.isRoomTemplate,
    isMobileView: state.mobileViewMode.isMobileView,
    faqProcessedEvent: state.manageDsrFaqContentOutcome.results?.faqProcessedEvent,
    manageFaqProcessingCompleted: state.manageDsrFaqContentOutcome?.results?.processingCompleted,
  };
}


const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      manageDsrFaqContent,
      resetManageDsrFaqContent
    },
    dispatch,
  )

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditQuestionAndAnswerModule));


